import { getEnv } from "./env";
import { ObjectValues } from "./types";

export const CITY_PLANNER_AREA_MAX = 10000;
export const CITY_PLANNER_MAX_SUBAREA_COUNT_FOR_DESIGN = 20;

// Define these here so we use the same image dimensions when requesting from fond service
// and adding the image to the map.
export const MAP_ICON_WIDTH = 20;
export const MAP_ICON_HEIGHT = 20;

// The buffer (in pixels) around a click location to search for features to select.
export const MAP_CLICK_BUFFER = 5;

export const STYLE_EDITOR_MIN_ZOOM = 0;
// The maximum zoom level for the layer. At zoom levels equal to or greater than the maxzoom, the layer will be hidden.
// Note: We set the maxzoom of the map to 22 so we require 23
export const STYLE_EDITOR_MAX_ZOOM = 23;

export const SECOND = 1_000;
export const MINUTE = 60 * SECOND;

export const MAX_OVERLAY_COUNT = 20;

/*
 * While we set our own password policy, cognito has a hard limit
 * of min 8 chars. Therefore given that we can change our policy at
 * any time, the only thing we can definitely know about an
 * existing password is that it must be > 8 chars. We could not
 * check this at all and let cognito validate the current password,
 * however the error messages it gives are terrible so we should
 * guard against them if possible.
 */
export const MIN_PASSWORD_LENGTH = 8;
export const SEARCH_KEY = "search";
export const REDIRECT_KEY = "redirect";
export const ACCOUNT_KEY = "account";
export const ACCOUNT_CONTEXT = "AccountContext";
export const LOGOUT_EVENT = "LogoutEvent";
export const DEMAND_MODEL_MODIFIED = "DemandModelModified";
export const RETURN_URL = "returnUrl";
export const FOLDER_ID = "folderId";
export const VERSION_ID = "versionId";
export const MULTI_PROJECT_ID = "multiProjectId";
export const MULTI_REPORT_FOLDER_ID = "multiReportFolderId";
export const BASE_MAP_BACKGROUND_OVERLAY = "background-overlay";
export const BASE_MAP_CUSTOM_OVERLAY_MARKER = "overlay-marker";
/**
 * LocalStorage key for analytics metadata
 */
export const ANALYTICS_METADATA = "AnalyticsMetadata";
export const INVITATION_ID_KEY = "invitation_id";
export const INVITATION_TOKEN_KEY = "token";

export const PAGINATION_KEY = "page";

export const TRIAL_BANNER_KEY = "HIDE_TRIAL_BANNER";
export const SOON_TO_EXPIRED_BANNER_KEY = "HIDE_SOON_TO_EXPIRE_BANNER";
export const EXPIRED_BANNER_KEY = "HIDE_EXPIRED_BANNER";
export const EXCEED_SUBSCRIPTION_QUOTA_KEY = "HIDE_EXCEED_SUBSCRIPTION_QUOTA_BANNER";
export const NO_LICENSE_ADMIN = "HIDE_NO_LICENSE_ADMIN_BANNER";
export const NO_LICENSE_MEMBER = "HIDE_NO_LICENSE_MEMBER_BANNER";
export const PENDING_INVITATION_BANNER_KEY = "HIDE_PENDING_INVITATION_BANNER";

export const RECENT_PROJECTS_KEY = "recentprojects";
export const STARRED_PROJECTS_KEY = "starredprojects";

/**
 * Query key for product types
 * The product types relates to product categories
 *
 * @see ProductCategory `fond/types/stripe.ts`
 */
export const PRODUCT_CATEGORY_KEY = "product";
export const PRODUCT_SELECTION_KEY = "selected";
export const PURCHASE_TYPE_KEY = "type";
export const BILLING_PERIOD_KEY = "period";
export const LICENSES_KEY = "licenses";
export const DATA_PACKS_KEY = "datapacks";
export const REMOVE_ADDON_KEY = "remove_addon";

export const TOS_URL = "https://biarrinetworks.com/-biarri-legal-terms";
export const HELP_CENTER_URL = "https://fondhelp.biarrinetworks.com/";
export const PRIVACY_URL = "https://biarrinetworks.com/-biarri-privacy-policy-";

export const HOME_FOLDER_ID = "home";
export const HOME_FOLDER_LABEL = "Home";
export const HOME_FOLDER_OPTION = {
  ID: HOME_FOLDER_ID,
  Name: HOME_FOLDER_LABEL,
  Path: [],
};

export const NO_PHASE = "NO_PHASE";

/** The minimum tool versions required for projects used to generate a report */
export const REPORT_CTS_MIN_TOOL_VERSION = "7.6.0";
export const REPORT_BOM_MIN_TOOL_VERSION = "7.8.1";

/** Fond Viral Design constants */
export const FOND_VIRAL_DESIGN_PREFIX = "[FondSampleDesign]";

/** Properties potentially added to a drawn features properties during drawing */
export const DRAWING_CONSTANTS = {
  Meta: "meta",
  Hover: "meta:hover",
  Unsnapped: "meta:unsnapped",
  Valid: "meta:valid",
  Intersect: "meta:intersect",
  ValidationErrors: "meta:validation-errors",
};

/** Properties to exclude from drawn features when saving */
export const DRAWING_PROPERTIES_TO_EXCLUDE = [
  "id",
  "layerId",
  "featureId",
  DRAWING_CONSTANTS.Meta,
  DRAWING_CONSTANTS.Hover,
  DRAWING_CONSTANTS.Unsnapped,
  DRAWING_CONSTANTS.Valid,
  DRAWING_CONSTANTS.Intersect,
  DRAWING_CONSTANTS.ValidationErrors,
];

/**
 * The default size for a page of results from the API.
 */
export const DEFAULT_PAGE_LIMIT = 50;

export const DisplayPageSize = {
  XLARGE: 100,
  LARGE: 50,
  DEFAULT: 20,
  SMALL: 10,
} as const;
export type DisplayPageSize = ObjectValues<typeof DisplayPageSize>;

/** HubSpot Forms */
export const HUBSPOT_PORTAL_ID = getEnv("REACT_APP_HUBSPOT_PORTAL_ID");
export const HUBSPOT_FORM_CONTACT_SALES = getEnv("REACT_APP_HUBSPOT_FORM_CONTACT_SALES");
export const HUBSPOT_FORM_PROVIDE_FEEDBACK = getEnv("REACT_APP_HUBSPOT_FORM_PROVIDE_FEEDBACK");

/**
 * The lowest tier level that we sell.
 */
export const SUBSCRIPTION_BASE_TIER_LEVEL = 1;
export const TRIAL_TIER = "trial-tier";
export const POPULAR_TIER = "tier-2";

/**
 * The product name for user licenses
 */
export const LICENSE_PRODUCT_NAME = "standard_license";

export const DATA_PACK_SIZE = 10_000;

export const TRIAL_DAYS = 14;
export const TRIAL_PREM_COUNT = 200;
export const TRIAL_LICENSE_COUNT = 2;
